import type React from "react"
import type { MouseEventHandler } from "react"
import { tv, type VariantProps } from "tailwind-variants"
import { disabledVariants } from "../../styles"
import { classNames } from "../../utils"
import {
  isClickableElement,
  isInteractiveElement,
  isLinkElement,
} from "../../utils/element"
import { Avatar, AvatarBadge, type AvatarProps } from "../Avatar"
import type { FlexProps } from "../Flex"
import { FlexColumn, type FlexColumnProps } from "../FlexColumn"
import { Link, type LinkProps, type Url } from "../Link"
import { Slot } from "../Slot"
import { TextBody, type TextBodyProps } from "../Text"
import { UnstyledButton } from "../UnstyledButton"
import { VerticalAligned, type VerticalAlignedProps } from "../VerticalAligned"

export type ItemRenderLinkComponent = React.ComponentType<
  Omit<LinkItemProps, "renderLink">
>

export type ItemPropsBase = FlexProps &
  VariantProps<typeof itemVariants> & {
    disabled?: boolean
    onClick?: MouseEventHandler<HTMLElement>
    renderLink?: ItemRenderLinkComponent
  }

const itemVariants = tv({
  base: "flex w-full items-center gap-3 border border-border-2 p-4",
  variants: {
    active: {
      true: "bg-bg-additional-2",
    },
    interactive: {
      true: "cursor-pointer hover:bg-bg-additional-1 active:bg-bg-additional-2",
    },
    disabled: {
      true: disabledVariants({ disabled: true }),
    },
    variant: {
      unstyled: "border-0 p-0",
      vertical: "flex-col items-start border-0 p-0",
    },
  },
})

type LinkItemProps = ItemPropsBase & {
  href: Url
} & LinkProps

export type DefaultItemProps = ItemPropsBase & {
  href?: undefined
  target?: string
}

export type ItemProps = DefaultItemProps | LinkItemProps

export function LinkItem({ renderLink, ...rest }: LinkItemProps) {
  const Comp = renderLink ?? Link

  return <Comp variant="unstyled" {...rest} />
}

export function Item({
  asChild,
  children,
  variant,
  interactive,
  renderLink,
  ...props
}: ItemProps) {
  const isInteractive = interactive ?? isInteractiveElement(props)

  const { className, disabled, href, target, onClick, ...rest } = props

  // onClick is added to contentProps so that it can be used in the
  // is{Link,Clickable}Element functions but it will be applied to the
  // outer Slot element because it is a valid prop for any element.
  const contentProps = { href, target, onClick, disabled }

  const renderContent = () => {
    if (asChild) {
      return children
    }

    if (isLinkElement(contentProps)) {
      return (
        <LinkItem
          disabled={contentProps.disabled}
          href={contentProps.href}
          renderLink={renderLink}
          target={contentProps.target}
        >
          {children}
        </LinkItem>
      )
    }

    if (isClickableElement(contentProps)) {
      return (
        <UnstyledButton disabled={contentProps.disabled}>
          {children}
        </UnstyledButton>
      )
    }

    return <div>{children}</div>
  }

  const isUnstyled = variant === "unstyled"

  return (
    <Slot
      className={classNames(
        itemVariants({
          active: !isUnstyled && rest.active,
          interactive: !(isUnstyled || disabled) && isInteractive,
          disabled: contentProps.disabled,
          variant,
        }),
        className,
      )}
      data-id="Item"
      onClick={onClick}
      {...rest}
    >
      {renderContent()}
    </Slot>
  )
}

export function ItemAvatar({ className, ...rest }: AvatarProps) {
  return (
    <Avatar
      className={classNames("order-1 shrink-0 text-text-primary", className)}
      data-id="ItemAvatar"
      {...rest}
    />
  )
}

export const ItemContent = ({ className, ...rest }: VerticalAlignedProps) => {
  return (
    <VerticalAligned
      className={classNames(
        "order-2 min-w-0 overflow-hidden",
        "flex-auto items-start self-stretch",
        className,
      )}
      data-id="ItemContent"
      {...rest}
    />
  )
}

export function ItemTitle(props: TextBodyProps) {
  return (
    <TextBody
      color="text-primary"
      data-id="ItemTitle"
      weight="semibold"
      {...props}
    />
  )
}

export function ItemDescription(props: TextBodyProps) {
  return (
    <TextBody color="text-secondary" data-id="ItemDescription" {...props} />
  )
}

export const ItemSide = ({ className, ...rest }: FlexColumnProps) => {
  return (
    <FlexColumn
      className={classNames(
        "order-3 max-w-[40%] overflow-hidden text-right",
        "flex-none justify-center self-stretch",
        className,
      )}
      {...rest}
    />
  )
}

export const ItemAction = ({ className, ...rest }: FlexColumnProps) => {
  return (
    <FlexColumn
      className={classNames("order-4 ml-3 justify-center", className)}
      {...rest}
    />
  )
}

export const ItemAvatarBadge = AvatarBadge
